var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { TeamsMeetingManager } from "../../../components/meeting-gui-hm";
import { MANAGER_MS_ROUTES } from "../../../constants/routes";
import { useApplicationContext } from "../../../context/application-context";
import apiClientServices, { setAuthorization } from "../../../api-client";
import { MEETING_ENDPOINTS } from "../../../utils/endpoints";
import { decryptText } from "../../../utils/encrypt-decrypt";
import HiringFeedbackV2 from "../../../components/hiring-feedback-v2";
import HmMeetingShell from "../hm-meeting-shell";
const ManagerMeetingUrlBased = () => {
    var _a, _b;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, tenantSettings } = useApplicationContext();
    const [token, setToken] = useState(null);
    const [msUserId, setMsUserId] = useState(null);
    const [meetingLink, setMeetingLink] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [decryptedPayload, setDecryptedPayload] = useState(null);
    const [hideFeedbackForm, setHideFeedbackForm] = useState(false);
    const colorPalette = (_a = tenantSettings === null || tenantSettings === void 0 ? void 0 : tenantSettings.tenantConfig) === null || _a === void 0 ? void 0 : _a.colorPalette;
    const spinnerIndicator = useMemo(() => (_jsx(LoadingOutlined, { style: {
            fontSize: 48,
            color: (colorPalette === null || colorPalette === void 0 ? void 0 : colorPalette.primaryColor) || "#1890ff",
        }, spin: true })), [colorPalette === null || colorPalette === void 0 ? void 0 : colorPalette.primaryColor]);
    // Function to decrypt and extract meeting data from link parameter
    const decryptMeetingData = () => {
        try {
            let link = searchParams.get("link");
            if (!link) {
                // Try to get from sessionStorage (stored during login init)
                return null;
            }
            if (link) {
                console.log("link in decryptMeetingData:", link);
                // Replace spaces with + (URL encoding)
                link = link.replaceAll(" ", "+");
                // Decrypt the link using the encryption key
                const encryptionKey = process.env.REACT_APP_PUBLIC_FEEDBACK_BIG;
                if (!encryptionKey) {
                    console.error("Encryption key not found in environment variables");
                    return null;
                }
                const stringPayload = decryptText(link, encryptionKey);
                if (!stringPayload) {
                    console.error("Failed to decrypt link");
                    return null;
                }
                const jsonPayload = JSON.parse(stringPayload);
                console.log("jsonPayload in decryptMeetingData:", jsonPayload);
                return jsonPayload;
            }
        }
        catch (error) {
            console.error("Error decrypting meeting data:", error);
        }
        return null;
    };
    // Function to fetch meeting link from API
    const getMeetingUrl = (decryptedData) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        try {
            setAuthorization(sessionStorage.getItem("token"));
            // Extract userId, courseId, and stageId from decrypted payload
            // The payload might have uid/cid/sid (decoded) or userId/courseId/stageId (decoded)
            const userId = decryptedData === null || decryptedData === void 0 ? void 0 : decryptedData.userId;
            const courseId = decryptedData === null || decryptedData === void 0 ? void 0 : decryptedData.courseId;
            const stageId = decryptedData === null || decryptedData === void 0 ? void 0 : decryptedData.stageId;
            const hmId = sessionStorage.getItem("userId");
            if (userId && stageId && courseId) {
                console.log("userId, stageId, courseId, hmId in getMeetingUrl:", userId, stageId, courseId, hmId);
                const response = yield apiClientServices.get(MEETING_ENDPOINTS.GET_HM_MEETING_LINK, {
                    params: Object.assign({ userId: userId.toString(), stageId: stageId.toString(), courseId: courseId.toString() }, (hmId && { hmId: hmId.toString() })),
                });
                if ((_d = (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.result) === null || _d === void 0 ? void 0 : _d.meetLink) {
                    setHideFeedbackForm(response.data.result.selectBotInterviewStageOnCaseStudyInvite ===
                        true);
                    return response.data.result.meetLink;
                }
            }
        }
        catch (error) {
            console.error("Error fetching meeting link from API:", error);
        }
        // Fallback to meetingLink from decrypted data if API fails
        return (decryptedData === null || decryptedData === void 0 ? void 0 : decryptedData.ml) || (decryptedData === null || decryptedData === void 0 ? void 0 : decryptedData.meetingLink) || null;
    });
    useEffect(() => {
        const initializeMeeting = () => __awaiter(void 0, void 0, void 0, function* () {
            // Get token and msUserId from sessionStorage (set by callback component)
            const storedToken = sessionStorage.getItem("manager_ms_token");
            const storedMsUserId = sessionStorage.getItem("manager_ms_user_id");
            if (!storedToken || !storedMsUserId) {
                // If token or msUserId not found, redirect to login init
                // Preserve the link parameter in the redirect
                const linkParam = searchParams.get("link");
                const redirectUrl = linkParam
                    ? `${MANAGER_MS_ROUTES.LOGIN_INIT}?link=${encodeURIComponent(linkParam)}`
                    : MANAGER_MS_ROUTES.LOGIN_INIT;
                navigate(redirectUrl);
                return;
            }
            setToken(storedToken);
            setMsUserId(storedMsUserId);
            // Decrypt meeting data from link parameter
            const decryptedData = decryptMeetingData();
            if (!decryptedData) {
                setError("Invalid or missing meeting link. Please check the URL.");
                setIsLoading(false);
                return;
            }
            // Fetch meeting link from API using decrypted data
            try {
                setDecryptedPayload(decryptedData);
                const meetingUrl = yield getMeetingUrl(decryptedData);
                if (meetingUrl) {
                    setMeetingLink(meetingUrl);
                }
                else {
                    setError("Meeting link not found. Please check the URL parameters.");
                }
            }
            catch (err) {
                console.error("Error initializing meeting:", err);
                setError("Failed to load meeting. Please try again.");
            }
            finally {
                setIsLoading(false);
            }
        });
        initializeMeeting();
    }, [searchParams, navigate]);
    const handleMeetingEnd = () => {
        // Clean up sessionStorage
        sessionStorage.removeItem("manager_ms_token");
        sessionStorage.removeItem("manager_ms_user_id");
        sessionStorage.removeItem("manager_ms_email");
        sessionStorage.removeItem("manager_ms_acs_user_id");
        sessionStorage.removeItem("manager_meeting_link");
        sessionStorage.removeItem("manager_meeting_link_param");
        console.log("Meeting ended");
    };
    if (isLoading) {
        return (_jsxs("div", Object.assign({ style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
            } }, { children: [_jsx(Spin, { size: "large", indicator: spinnerIndicator }), _jsx("div", Object.assign({ style: { marginTop: 16 } }, { children: "Loading meeting..." }))] })));
    }
    if (error) {
        return (_jsxs("div", Object.assign({ style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
            } }, { children: [_jsx("div", Object.assign({ style: { color: "red", marginBottom: 16 } }, { children: error })), _jsx("button", Object.assign({ onClick: () => navigate(-1) }, { children: "Go Back" }))] })));
    }
    if (!token || !msUserId || !meetingLink) {
        return (_jsxs("div", Object.assign({ style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
            } }, { children: [_jsx("div", Object.assign({ style: { marginBottom: 16 } }, { children: "Missing required information. Please try again." })), _jsx("button", Object.assign({ onClick: () => {
                        const tenantUrl = window.location.origin;
                        navigate(`${MANAGER_MS_ROUTES.LOGIN_INIT}?tenantUrl=${encodeURIComponent(tenantUrl)}`);
                    } }, { children: "Retry Login" }))] })));
    }
    return (_jsx(HmMeetingShell, { hideFeedbackForm: hideFeedbackForm, meeting: _jsx(TeamsMeetingManager, { userId: {
                microsoftTeamsUserId: msUserId,
                isAnonymous: false,
            }, token: token, locator: meetingLink, displayName: user ? `${user.firstName} ${user.lastName}` : "Manager", callInvitationURL: meetingLink, onMeetingEnd: handleMeetingEnd }), feedback: _jsx(HiringFeedbackV2, { userId: decryptedPayload === null || decryptedPayload === void 0 ? void 0 : decryptedPayload.userId, courseId: decryptedPayload === null || decryptedPayload === void 0 ? void 0 : decryptedPayload.courseId, stageId: decryptedPayload === null || decryptedPayload === void 0 ? void 0 : decryptedPayload.stageId, panelInterviewId: (_b = decryptedPayload === null || decryptedPayload === void 0 ? void 0 : decryptedPayload.panelInterviewId) !== null && _b !== void 0 ? _b : decryptedPayload === null || decryptedPayload === void 0 ? void 0 : decryptedPayload.interviewId, enableInviteAdditionalHm: true }) }));
};
export default ManagerMeetingUrlBased;
